import { useState } from "react";

import { Ticket, TicketCategory } from "@signalist/shared-types";

import { PermissionRoles } from "@/enum";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import {
	SUPPORT_TICKETS_PAGE_SIZE,
	useGetTicketsQuery,
} from "@/pages/Private/redux/tickets/tickets.api";

import { FeaturesCodes } from "../../Users/enum/codes";
import { filterTickets } from "../utils/ticketFilters";
import { countTicketTabs, SupportTicketTab, TicketTabCounts } from "../utils/ticketTabs";

/**
 * The Support list's state and derivation. One request brings the most recently updated page of
 * tickets; the tabs, their counts, the search and the category filter are computed from it
 * (PREC-3937 decision 4), because the customer API supports neither.
 *
 * The counts are computed **after** search / category / scope, so a tab never promises rows the
 * current filters would hide.
 */
export const useSupportTickets = () => {
	const { user } = useAppSelector(authSelector);
	const { hasPermissions } = useHasPermissions();
	const isFeatureActive = useIsFeatureActive(FeaturesCodes.SUPPORT_TICKETS);

	const [tab, setTab] = useState(SupportTicketTab.ALL);
	const [search, setSearch] = useState("");
	const [category, setCategory] = useState<TicketCategory | null>(null);
	const [onlyMine, setOnlyMine] = useState(false);

	const isCompanyAdmin = hasPermissions([PermissionRoles.COMPANY]);

	const { data, isLoading } = useGetTicketsQuery(
		{ page: 1, limit: SUPPORT_TICKETS_PAGE_SIZE },
		{ skip: !isFeatureActive }
	);

	const tickets: Ticket[] = data?.data ?? [];
	const onlyMineUserId = isCompanyAdmin && onlyMine ? (user?.id ?? null) : null;
	const itemCount = Number(data?.meta.itemCount ?? 0);

	// Everything except the tab, so the tab counts describe the same rows the tabs would show.
	const scoped = filterTickets(tickets, {
		tab: SupportTicketTab.ALL,
		search,
		category,
		onlyMineUserId,
	});

	const counts: TicketTabCounts = countTicketTabs(scoped);

	return {
		// The scoped set narrowed to the active tab — the other filters already applied above.
		rows: filterTickets(scoped, { tab, search: "", category: null, onlyMineUserId: null }),
		counts,
		itemCount,
		isTruncated: itemCount > SUPPORT_TICKETS_PAGE_SIZE,
		hasAnyTicket: tickets.length > 0,
		isLoading,
		tab,
		setTab,
		search,
		setSearch,
		category,
		setCategory,
		onlyMine,
		setOnlyMine,
		isCompanyAdmin,
		companyName: user?.company?.name ?? "",
	};
};

export type SupportTicketsController = ReturnType<typeof useSupportTickets>;
